if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.setHeaderCartIconAccessibility();
    }

    init() {
      if (!this.isInitialized) {
        window.loadTemplateContent(this);

        requestAnimationFrame(() => {
          // Bind various things
          this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.moveCssLinks();
          this.isInitialized = true;
        });
      }
    }

    reload() {
      const sections = this.getSectionsToRender().map((section) => section.id);
      fetch(`?sections=${sections}`)
        .then((response) => response.json())
        .then((response) => {
          this.renderContents({
            sections: {
              ...response
            }
          }, true);
        })
        .catch((e) => {
          console.error(e);
        });
    }

    // RS0805-FIX-D01 (regression sweep 2026-08-05).
    //
    // While `settings.lazy_load_js` is on, snippets/cart-drawer.liquid wraps
    // this element's entire contents in <template class="deferred">, so until
    // init() runs there is no #CartDrawer, no .drawer__inner and no
    // <cart-drawer-items> to render into — renderContents() above cannot be
    // used, and cart.js's own renderContents loop skips the drawer for the same
    // reason. The drawer then hydrates from whatever the page shipped with,
    // which is why a cart mutated on /cart used to open a drawer still showing
    // the removed line and the pre-mutation total.
    //
    // cart.js asks for the `cart-drawer` section on any mutation made while the
    // drawer is deferred (four distinct sections — the same count /cart already
    // sends once the drawer has hydrated, see the note there) and hands the
    // rendered markup here. Swapping innerHTML keeps the fresh copy deferred:
    // the response carries its own <template class="deferred">, so nothing is
    // parsed, no stylesheet or script inside it loads, and the lazy-loading
    // behaviour is preserved exactly — the drawer simply hydrates from a
    // current snapshot instead of a stale one.
    //
    // Returns true only when a swap actually happened, so callers can tell
    // "installed" from "nothing to do" without inspecting the DOM.
    installDeferredSection(html) {
      if (typeof html !== 'string' || html === '') return false;
      // Live contents mean the drawer is hydrated and the normal section
      // rendering path owns it; never overwrite a drawer the shopper may have
      // open or interacted with.
      if (this.querySelector('cart-drawer-items')) return false;

      const fresh = this.getSectionDOM(html, 'cart-drawer');
      if (!fresh) return false;

      this.innerHTML = fresh.innerHTML;
      // The empty/non-empty class lives on the element itself, outside the
      // swapped markup, so take the server's word for it.
      this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));

      // Defensive: if a future settings change ever ships the drawer without
      // the deferred template, the swap installs live markup, which must be
      // bound (overlay click, Escape) exactly as a normal hydration would be.
      if (!this.isInitialized && !this.querySelector('template.deferred')) this.init();

      return true;
    }

    moveCssLinks() {
      const cssLinks = this.querySelectorAll('link[rel="stylesheet"][href*=".css"]');
      cssLinks.forEach(link => {
        // Check if the link already exists in the document head
        const href = link.href;
        if (!document.head.querySelector(`link[href="${href}"]`)) {
          // Clone the link element
          const newLink = link.cloneNode(true);
          // Append the cloned link to the document head
          document.head.appendChild(newLink);
        }
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      // RG22-V2-002 / C-4: never show a drawer whose last render failed. Refresh it
      // first; refreshStaleThenOpen() calls back into open() once it is current.
      if (this.stale) {
        this.refreshStaleThenOpen(triggeredBy);
        return;
      }

      this.init();

      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.removeDeleteTabIndex();

      // QG 2026-07-28 (D-R-001 fix): the transitionend below never fires when
      // prefers-reduced-motion zeroes .drawer__inner's transform transition (the
      // .motion-reduce class hardcoded on this element and its children), so
      // trapFocus/cartOpen never ran and focus stayed behind the open dialog.
      // openFocusTrapped guards the two listeners below against a double-fire —
      // whichever fires first (the real transitionend, or the ~duration-default
      // fallback timer) runs the trap once; the other is a no-op.
      let openFocusTrapped = false;
      const runOpenFocusTrap = () => {
        if (openFocusTrapped) return;
        openFocusTrapped = true;

        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        publish(PUB_SUB_EVENTS.cartOpen);
      };

      this.addEventListener('transitionend', runOpenFocusTrap, {once: true});
      setTimeout(runOpenFocusTrap, 300);

      document.body.classList.add('overflow-hidden');
    }

    // RG22-V2-002 / C-4: bring a stale drawer up to date before it is shown.
    //
    // One Section Rendering request for the drawer and the header bubble. If the drawer
    // section renders, renderContents() syncs `is-empty`, clears `stale`, and the drawer
    // opens with the real lines. If it does not (the upstream null has been measured to
    // persist for a whole session), the drawer cannot be trusted, so the shopper goes to
    // the server-rendered cart page, which is always current. That is where the cart
    // icon's own href points anyway, so it is the link's native behaviour, not a detour.
    refreshStaleThenOpen(triggeredBy) {
      if (this.refreshingStale) return;
      this.refreshingStale = true;
      const ids = this.getSectionsToRender().map((section) => section.id).join(',');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(`${root}?sections=${ids}`)
        .then((response) => response.json())
        .then((sections) => this.renderContents({ sections }, true))
        .catch((e) => console.error(e))
        .finally(() => {
          this.refreshingStale = false;
          if (this.stale) {
            window.location.href = window.routes.cart_url;
            return;
          }
          // renderContents() re-binds via setTimeout; open on the next tick after it.
          setTimeout(() => this.open(triggeredBy));
        });
    }

    // RS0930-CONT-D03 (RC-04): cart.js asked for this section in a /cart/change and got no
    // markup back (upstream section-render failure). Re-fetch it once. On success the drawer
    // shows the server's current state (empty state included) and, if it is open, focus is
    // put back inside it. If the render fails again the drawer cannot be trusted: an open
    // drawer hands over to the server-rendered /cart page (the same fallback open() uses for
    // a stale drawer); a closed one stays `stale` so the next open() refreshes it first.
    recoverMissingSection() {
      this.stale = true;
      if (this.recoveringSection) return this.recoveringSection;
      const ids = this.getSectionsToRender().map((section) => section.id).join(',');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      this.recoveringSection = fetch(`${root}?sections=${ids}`)
        .then((response) => response.json())
        .then((sections) => this.renderContents({ sections }, true))
        .catch((e) => console.error(e))
        .finally(() => {
          this.recoveringSection = null;
          if (!this.classList.contains('active')) return;
          if (this.stale) {
            window.location.href = window.routes.cart_url;
            return;
          }
          // renderContents() re-binds on a timeout; restore focus on the tick after it.
          setTimeout(() => {
            if (this.contains(document.activeElement) && document.activeElement !== document.body) return;
            const emptyDrawer = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : null;
            if (emptyDrawer) {
              trapFocus(emptyDrawer, emptyDrawer.querySelector('a, button'));
              return;
            }
            const closeButton = [...this.querySelectorAll('.drawer__close')].find(
              (button) => button.getClientRects().length > 0
            );
            const container = document.getElementById('CartDrawer') || this;
            trapFocus(container, closeButton || container);
          });
        });
      return this.recoveringSection;
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      publish(PUB_SUB_EVENTS.cartClose);
    }

    /**
     * Ensure all remove buttons can be tabbed to
     */
    removeDeleteTabIndex() {
      this.querySelectorAll('.cart-remove-button').forEach((button) => {
        button.removeAttribute('tabindex')
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState, preventOpen = false) {
      // RG22-V2-002 / C-4 (fixed 2026-09-23, second attempt): this method is now the
      // ONLY owner of the drawer's empty/non-empty state, and it takes that state from
      // the freshly rendered section, never from an assumption.
      //
      // Measured root cause: the class that drives the empty presentation lives on this
      // <cart-drawer> element (`cart-drawer.is-empty …` in component-cart-drawer.css).
      // `.drawer__inner` never carries it in this theme's markup, so the first V2-002 fix,
      // which guarded `.drawer__inner`, changed nothing. Meanwhile product-form.js stripped
      // `is-empty` from this element in its `.finally()` on every add, whether or not the
      // drawer's contents were refreshed. After a withheld render (RG22-ORCH-005 null
      // payload), a manual open from the cart icon then showed "You're $50.00 from
      // shipping" with zero lines while the cart held a $180.95 item. That strip is gone
      // (product-form.js), and the class is synced below from the rendered section.
      //
      // A drawer whose own section failed to render is marked `stale`; `open()` refreshes
      // a stale drawer before showing it and, if the refresh fails too, sends the shopper
      // to the server-rendered /cart page instead of showing contents it cannot vouch for.
      this.productId = parsedState.id;
      const missingSections = [];
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) {
          // With `lazy_load_js` on, the drawer's markup sits in a deferred <template>
          // until init(), so #CartDrawer is not in the DOM yet. Install the fresh section
          // into the template instead (same path cart.js uses, RS0805-FIX-D01), or mark
          // the drawer stale if there is nothing fresh to install.
          if (section.id === 'cart-drawer') {
            const html = parsedState.sections?.[section.id];
            if (!this.installDeferredSection(html) && this.querySelector('template.deferred')) {
              missingSections.push(section);
            }
          }
          return;
        }

        // RG22-ORCH-005 (2026-09-22): guard the PAYLOAD, not just the element.
        //
        // Measured on the dev preview: /cart/add answers 200 with a `sections` object
        // that contains both expected keys, but `sections['cart-icon-bubble']` is
        // sometimes `null` (3 of 6 identical trials). `getSectionInnerHTML` then ran
        // `null.replace(...)` and threw. Because the throw escapes this forEach, it
        // also skipped everything BELOW it in renderContents -- including `this.open()`
        // -- so one null section silently took down the whole post-add UI: no bubble,
        // and no drawer either, although the drawer's own HTML had arrived intact. The
        // line still reached the cart, so the customer got no feedback at all for an
        // add that actually succeeded.
        //
        // A section that fails to render is now skipped on its own; every other section
        // still updates and the drawer still opens. This degrades gracefully instead of
        // failing whole, which matters because the null originates upstream (Section
        // Rendering) and the theme cannot prevent it.
        const sectionHTML = parsedState.sections?.[section.id];
        if (typeof sectionHTML !== 'string' || sectionHTML.length === 0) {
          missingSections.push(section);
          return;
        }

        const rendered = this.getSectionInnerHTML(sectionHTML, section.selector);
        if (rendered === null) {
          missingSections.push(section);
          return;
        }
        sectionElement.innerHTML = rendered;

        // The element carrying `is-empty` is outside the swapped markup, so take the
        // server's word for it from the same payload (mirrors installDeferredSection).
        if (section.id === 'cart-drawer') {
          const fresh = this.getSectionDOM(sectionHTML, 'cart-drawer');
          if (fresh) this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
        }
      });

      // Skipping a missing section stops the throw, but it is not enough on its own:
      // measured, the drawer then OPENED still showing its pre-add markup -- "Your cart,
      // you're $50.00 from shipping", zero line items -- while the cart really held one.
      // Telling a customer their cart is empty when it is not is worse than not opening.
      //
      // A re-request does NOT help: measured, the null is persistent for the whole
      // session (cart holds the line, yet that session's section requests keep returning
      // null, unchanged after a 1.2s delay), so it is not a write/render race. Therefore
      // the only honest response is to withhold the drawer when its own contents could
      // not be refreshed, while still applying every section that DID render.
      const drawerSectionFailed = missingSections.some((s) => s.id === 'cart-drawer');

      // RG22-V2-002: remember that the drawer's markup no longer matches the cart, so a
      // later manual open refreshes it first (see open()). A successful render clears it.
      this.stale = drawerSectionFailed;

      setTimeout(() => {
        window.loadTemplateContent(this);
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      });

      this.removeDeleteTabIndex();

      if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen && !drawerSectionFailed) {
        setTimeout(() => {
          this.open();
        });
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      // RG22-ORCH-005: null-safe. Returns null rather than throwing when the section
      // payload is missing, or when the parsed markup does not contain `selector`.
      // Callers treat null as "leave this section alone". Both failures are upstream
      // conditions the theme cannot prevent, and neither justifies aborting the caller.
      if (typeof html !== 'string') return null;
      html = html.replace(/<template\b[^>]*>|<\/template>/g, '');
      const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return found ? found.innerHTML : null;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.whenDefined('cart-items').then(() => {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          // A06 / NEW-CART-02: fill the drawer's status region with the localized
          // "New estimated total: $X". Listed AFTER 'CartDrawer' because that entry replaces
          // .drawer__inner, which contains this element. `cart-live-region-text` is already
          // requested on /cart, so the distinct-section count (ceiling 5) does not grow.
          {
            id: 'CartDrawer-LiveRegionText',
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  });
}
